import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'

import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { getAssetLink } from '@/helpers/get-asset-link'
import { PackageModel } from '@/models'
import { getPackageBadgeColor, getPackageStatusLabels } from '@/pages/packages'
import { formatDateOnly, formatTimeOnly } from '@/utils'

import * as S from './package-session-details.styled'

interface GetPackageSessionDetailsColumnsParams {
  timezone?: string
  t: TFunction<['packages']>
}

export const getPackageSessionDetailsColumns = ({
  timezone = 'America/Chicago',
  t,
}: GetPackageSessionDetailsColumnsParams): TableColumn<PackageModel>[] => [
  {
    id: 'packageId',
    render: (packageItem) => packageItem.id,
    title: t('packages:sessionDetails.columns.packageId'),
    width: '10rem',
  },
  {
    id: 'checkedIn',
    render: (packageItem) => {
      if (!packageItem.checkinAt) return EMPTY_VALUE
      const zonedDate = toZonedTime(packageItem.checkinAt, timezone)
      return `${formatDateOnly(zonedDate)}\n${formatTimeOnly(zonedDate)}`
    },
    title: t('packages:sessionDetails.columns.checkedIn'),
    width: '13rem',
  },
  {
    id: 'checkedOut',
    render: (packageItem) => {
      if (!packageItem.checkoutAt) return EMPTY_VALUE
      const zonedDate = toZonedTime(packageItem.checkoutAt, timezone)
      return `${formatDateOnly(zonedDate)}\n${formatTimeOnly(zonedDate)}`
    },
    title: t('packages:sessionDetails.columns.checkedOut'),
    width: '13rem',
  },
  {
    id: 'resident',
    render: (packageItem) => (
      <S.ResidentCell>
        <Avatar
          src={getAssetLink({ file: packageItem.residentAvatar, path: 'chitown-avatar/avatar' })}
          alt={packageItem.residentName}
          size="small"
        />
        <S.ResidentInfo>
          <Typography as="span" color="black700" size="text-small" weight="medium" breakLines={1}>
            {packageItem.residentName || EMPTY_VALUE}
          </Typography>
          <Typography as="span" color="black500" size="text-small">
            {t('packages:sessionDetails.columns.residentUnit', {
              unit: packageItem.residentUnit || EMPTY_VALUE,
            })}
          </Typography>
        </S.ResidentInfo>
      </S.ResidentCell>
    ),
    title: t('packages:sessionDetails.columns.resident'),
    width: '20rem',
  },
  {
    id: 'status',
    render: (packageItem) => {
      if (!packageItem.status) return EMPTY_VALUE
      return (
        <Badge color={getPackageBadgeColor(packageItem.status)} variant="light" size="small">
          {getPackageStatusLabels(t)[packageItem.status]}
        </Badge>
      )
    },
    title: t('packages:sessionDetails.columns.status'),
    width: '20rem',
  },
]
